/**
 * Bookd - PWA Install Prompt Component
 * Shows a banner prompting users to install the app on their device
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone, Zap, Bell, Wifi } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedDate = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedDate 
      ? (new Date() - dismissedDate) / (1000 * 60 * 60 * 24) 
      : Infinity;

    // Show prompt if not standalone and not dismissed within 7 days
    if (!standalone && daysSinceDismissed > 7) {
      // For Android/Desktop Chrome - listen for beforeinstallprompt
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        // Show after a short delay (let user see the app first)
        setTimeout(() => setShowPrompt(true), 3000);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstall);

      // For iOS - show instructions prompt after delay
      if (iOS) {
        setTimeout(() => setShowPrompt(true), 5000);
      }

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Trigger the native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  // Don't show if already installed or prompt not ready
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-500">
      <div className="max-w-lg mx-auto bg-gradient-to-r from-primary/95 to-violet-600/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-5 w-5 text-white/70" />
        </button>

        <div className="p-5">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-white/20">
              <Smartphone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-lg">Install Bookd</h3>
              <p className="text-white/70 text-sm">Add to your home screen</p>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="flex flex-col items-center p-2 rounded-lg bg-white/10">
              <Zap className="h-4 w-4 text-yellow-300 mb-1" />
              <span className="text-xs text-white/80 text-center">Faster</span>
            </div>
            <div className="flex flex-col items-center p-2 rounded-lg bg-white/10">
              <Bell className="h-4 w-4 text-green-300 mb-1" />
              <span className="text-xs text-white/80 text-center">Notifications</span>
            </div>
            <div className="flex flex-col items-center p-2 rounded-lg bg-white/10">
              <Wifi className="h-4 w-4 text-blue-300 mb-1" />
              <span className="text-xs text-white/80 text-center">Offline</span>
            </div>
          </div>

          {/* Action */}
          {isIOS ? (
            // iOS instructions - Safari share is in the browser toolbar
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-white text-sm mb-3">
                To install on iOS Safari:
              </p>
              <ol className="text-white/80 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs shrink-0 mt-0.5">1</span>
                  <span>Tap the <strong className="text-white">Share icon</strong> (square with arrow) in Safari's toolbar at the bottom of your screen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs shrink-0 mt-0.5">2</span>
                  <span>Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs shrink-0 mt-0.5">3</span>
                  <span>Tap <strong className="text-white">"Add"</strong> in the top right corner</span>
                </li>
              </ol>
              <Button
                onClick={handleDismiss}
                variant="secondary"
                className="w-full mt-4 rounded-xl bg-white text-primary hover:bg-white/90"
              >
                Got it!
              </Button>
            </div>
          ) : (
            // Android/Desktop - native install
            <div className="flex gap-3">
              <Button
                onClick={handleDismiss}
                variant="ghost"
                className="flex-1 text-white/70 hover:text-white hover:bg-white/10 rounded-xl"
              >
                Not now
              </Button>
              <Button
                onClick={handleInstall}
                className="flex-1 bg-white text-primary hover:bg-white/90 rounded-xl font-semibold"
                data-testid="pwa-install-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Install
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
